<script setup lang="ts">
import AgTableClient from '@/modules/common/components/ag-table/AgTableClient.vue';
import { ColDef, IRowNode, ManagedGridOptionKey, RowClassParams } from 'ag-grid-enterprise';
import { computed, onBeforeMount, onMounted, shallowRef, watch } from 'vue';
import { FrontendExecution } from '@/connect/gen/modules/apiengine/services/executions/executions_pb';
import { useSocketStore } from '@/store/socket-store';
import { useSecuritiesStore } from '@/store/securities-store';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace';
import { marketplaceService } from '@/connect/services/marketplace';
import { saveFileFromConnectEndpoint } from '@/utils/helpers/download';
import { SettlementType } from '@/connect';
import { store as vuex } from '@/store/store';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import i18n from '@/localisation/i18n';
import { SortModelItem } from 'ag-grid-enterprise';
import { throttle } from 'lodash';

const props = withDefaults(
  defineProps<{ filteredByCusips?: string[]; asBroker?: boolean; companyIdFilter?: string }>(),
  {
    filteredByCusips: () => [],
    asBroker: false,
    companyIdFilter: '',
  }
);

const snackbar = useSnackbarManager();

const socketStore = useSocketStore();
const securitiesStore = useSecuritiesStore();

const loginState = computed(() => vuex.state.loginState);

const executions = shallowRef<FrontendExecution[]>([]);

const sortOn = shallowRef<SortModelItem>({
  colId: 'execTime',
  sort: 'desc',
});

const socketConnected = computed(() => vuex.state.socketConnected);
watch([socketConnected, () => props.companyIdFilter], async () => {
  await queryData();
});

const showAgreementCols = computed(() => !props.asBroker || props.companyIdFilter !== '');

const showCompanyCols = computed(() => props.asBroker && props.companyIdFilter === '');

const showCustomExportAndAckButon0101 = computed(
  () =>
    !!(
      (props.asBroker && props.companyIdFilter) ||
      loginState.value.user?.clientFlags?.includes('custom_executions_export_and_ack_report_0101')
    )
);

const showCustomExportAndAckButon7273 = computed(
  () =>
    !!(
      (props.asBroker && props.companyIdFilter) ||
      loginState.value.user?.clientFlags?.includes('custom_executions_export_and_ack_report_7273')
    )
);

const showCustomExportAndAckButonESEC = computed(
  () =>
    !!(
      (props.asBroker && props.companyIdFilter) ||
      loginState.value.user?.clientFlags?.includes('custom_executions_export_and_ack_report_esec')
    )
);

const colConfig = { filter: true };
const columnDefs = computed<ColDef[]>(() => [
  ...(props.asBroker ? [] : [cols.checkbox()]),
  cols.updatedAt(colConfig),
  cols.myStatus(colConfig),
  cols.contraStatus(colConfig),
  ...(showCompanyCols.value ? [cols.company(colConfig)] : []),
  cols.executionsSide(colConfig),
  cols.counterparty(colConfig),
  cols.profitCenter({ hiddenByDefault: true, ...colConfig }),
  ...(showAgreementCols.value ? [cols.agreementShortName(colConfig)] : []),
  cols.ticker(colConfig),
  cols.cusip(colConfig),
  cols.isin({ hiddenByDefault: true, ...colConfig }),
  cols.securityCategory({ hiddenByDefault: true, ...colConfig }),
  cols.securityType({ hiddenByDefault: true, ...colConfig }),
  cols.executedQuantity(colConfig),
  cols.fee(colConfig),
  cols.rebate(colConfig),
  cols.percentagePar({ hiddenByDefault: true, ...colConfig }),
  cols.unitPrice({ hiddenByDefault: true, ...colConfig }),
  cols.settlementPrice(colConfig),
  cols.settlementAmount(colConfig),
  cols.pricingCurrency(colConfig),
  cols.billingCurrency({ hiddenByDefault: true, ...colConfig }),
  cols.settlementType(colConfig),
  cols.dividendRequired({ hiddenByDefault: true, ...colConfig }),
  cols.termType({ hiddenByDefault: true, ...colConfig }),
  cols.termStartDate({ hiddenByDefault: true, ...colConfig }),
  cols.termEndDate({ hiddenByDefault: true, ...colConfig }),
  cols.independentAmountRateAsPercentCollateral(colConfig),
  cols.roundingRule({ hiddenByDefault: true, ...colConfig }),
  cols.collateralType(colConfig),
  cols.collateralMethod({ hiddenByDefault: true, ...colConfig }),
  cols.rwaBucket({ hiddenByDefault: true, ...colConfig }),
  cols.lastText({ hiddenByDefault: true, ...colConfig }),
  cols.tradeDate(colConfig),
  cols.settlementDate(colConfig),
  cols.acknowledgedAt(colConfig),
  cols.bookingAccount({ hiddenByDefault: true, ...colConfig }),
  cols.counterpartyBookingAccount({ hiddenByDefault: true, ...colConfig }),
  cols.tradeType({ hiddenByDefault: true, ...colConfig }),
  cols.execTime(colConfig),
  ...(showAgreementCols.value ? [cols.agreementDisplayId(colConfig)] : []),
  cols.executionId(colConfig),
  cols.auroraLoanID(colConfig),
  cols.orderDisplayId(colConfig),
  cols.orderCreatedBy({ hiddenByDefault: false, ...colConfig }),
  cols.orderLastUpdatedBy({ hiddenByDefault: false, ...colConfig }),
]);

const shouldNotQueryDataWhenItemsAreSelected = shallowRef(false);

const selectedItems = shallowRef<FrontendExecution[]>([]);

watch(selectedItems, async () => {
  if (selectedItems.value.length === 0) {
    // query now to ensure we are synced
    shouldNotQueryDataWhenItemsAreSelected.value = false;
    await queryData();
  } else {
    // don't query until we unselect
    shouldNotQueryDataWhenItemsAreSelected.value = true;
  }
});

const gridConfig = shallowRef<{
  setGridOption: (option: ManagedGridOptionKey, value: unknown) => void;
  resetSelection: () => void;
  redrawRows: () => void;
} | null>(null);

const rowCount = shallowRef(0);

function onReady(config: {
  setGridOption: (option: ManagedGridOptionKey, value: unknown) => void;
  resetSelection: () => void;
  redrawRows: () => void;
}) {
  gridConfig.value = config;
}

function getRowId(order: FrontendExecution): string {
  return `${order.execId}-${order.side}`;
}

function getRowClass(params: RowClassParams<FrontendExecution>): string {
  return !params.data?.acknowledgedAt ? 'unacknowledged-row' : '';
}

function isRowSelectable(rowNode: IRowNode<FrontendExecution>): boolean {
  return (
    showCustomExportAndAckButon0101.value ||
    showCustomExportAndAckButon7273.value ||
    showCustomExportAndAckButonESEC.value ||
    !rowNode.data?.acknowledgedAt
  );
}

const canMarkCancelled = computed(
  () =>
    selectedItems.value.length > 0 &&
    selectedItems.value.every((item) => item.settlementType !== SettlementType.NSCC)
);

function resetSelection() {
  gridConfig.value?.resetSelection();
  selectedItems.value = [];
}

async function markCancelledSelected(): Promise<void> {
  const selectedIds = selectedItems.value.map((item) => item.execId);
  await marketplaceService.cancelExecutionRecords({ executionIds: selectedIds });
  resetSelection();
  snackbar.show({ message: `Cancelled ${selectedIds.length} executions` });
}

async function acknowledgeSelected() {
  const selectedIds = selectedItems.value.map((item) => item.execId);
  await marketplaceService.acknowledgeExecutions({ executionIds: selectedIds });
  resetSelection();
  snackbar.show({ message: `Acknowledged ${selectedIds.length} executions` });
}

async function customExportAndAck0101() {
  try {
    const selectedIds = selectedItems.value.map((item) => item.execId);
    await saveFileFromConnectEndpoint(
      marketplaceService.customExportAndAck0101({
        executionIds: selectedIds,
        asCompanyId: props.companyIdFilter,
      }),
      'executions',
      'xlsx'
    );
    resetSelection();
    snackbar.show({ message: 'Executions downloaded and acknowledged' });
  } catch {
    snackbar.show({ message: 'Failed to download and acknowledge executions' });
  }
}

async function customExportAndAck7273() {
  try {
    const selectedIds = selectedItems.value.map((item) => item.execId);
    await saveFileFromConnectEndpoint(
      marketplaceService.customExportAndAck7273({
        executionIds: selectedIds,
        asCompanyId: props.companyIdFilter,
      }),
      'executions',
      'xlsx'
    );
    gridConfig.value?.resetSelection();
    selectedItems.value = [];
    snackbar.show({ message: 'Executions downloaded and acknowledged' });
  } catch {
    snackbar.show({ message: 'Failed to download and acknowledge executions' });
  }
}

async function customExportAndAckESEC() {
  try {
    const selectedIds = selectedItems.value.map((item) => item.execId);
    await saveFileFromConnectEndpoint(
      marketplaceService.customExportAndAckESEC({
        executionIds: selectedIds,
        asCompanyId: props.companyIdFilter,
      }),
      'executions',
      'xlsx'
    );
    gridConfig.value?.resetSelection();
    selectedItems.value = [];
    snackbar.show({ message: 'Executions downloaded and acknowledged' });
  } catch {
    snackbar.show({ message: 'Failed to download and acknowledge executions' });
  }
}

async function queryData() {
  if (shouldNotQueryDataWhenItemsAreSelected.value) return;

  const result = await marketplaceService.queryExecutions(
    {
      // server pagination does not make sense when the client filters and sorts the results
      pageNumber: -1,
      pageLimit: -1,
    },
    props.companyIdFilter
  );

  if (!result.success) {
    snackbar.show({ message: i18n.t(result.error, result.details) as string });
    return;
  }

  securitiesStore.addSecurities(result.data.instruments);

  let resultExecutions = result.data.execRecs;
  if (props.filteredByCusips.length > 0) {
    resultExecutions = resultExecutions.filter((execution) =>
      props.filteredByCusips.includes(execution.cusip)
    );
  }

  rowCount.value = resultExecutions.length;

  // setting directly should be working:
  // this.executions = executions;
  // because inside AgTableClient, we are already programmatically updating the row data
  // however, this is causing cols reordering and floating filter reset
  // because of the setInterval of 1s;
  gridConfig.value?.setGridOption('rowData', resultExecutions);

  // row background color does not update unless we redraw the rows
  gridConfig.value?.redrawRows();
}

const queryDataThrottled = throttle(queryData, 2000, {
  leading: false,
  trailing: true, // because we want the most recent data
});

onBeforeMount(() => {
  socketStore.useEventHandler('refreshLenderLoan', queryDataThrottled);
  socketStore.useEventHandler('refreshBorrowerLoan', queryDataThrottled);
  socketStore.useEventHandler('refreshOmsUnackedExecutions', queryDataThrottled);
});

onMounted(queryData);
</script>

<template>
  <div class="flex d-flex flex-column">
    <div
      class="d-flex align-center pa-4"
      :class="{ 'no-selected-items': selectedItems.length === 0 || asBroker }"
    >
      <span class="selected-items-actions mr-4">
        <span>{{ selectedItems.length }} / {{ rowCount }} selected</span>
        <VBtn class="ml-4" color="green" small @click="acknowledgeSelected"> Acknowledge </VBtn>
        <VBtn v-if="canMarkCancelled" class="ml-4" color="red" small @click="markCancelledSelected">
          Mark Cancelled
        </VBtn>
      </span>
      <span class="export-actions">
        <VBtn v-if="showCustomExportAndAckButon0101" small @click="customExportAndAck0101">
          0101 Export <template v-if="!asBroker">And Ack</template>
          <VIcon class="ml-1" small> mdi-download </VIcon>
        </VBtn>
        <VBtn v-if="showCustomExportAndAckButon7273" small @click="customExportAndAck7273">
          7273 Export <template v-if="!asBroker">And Ack</template>
          <VIcon class="ml-1" small> mdi-download </VIcon>
        </VBtn>
        <VBtn v-if="showCustomExportAndAckButonESEC" small @click="customExportAndAckESEC">
          ESEC Export <template v-if="!asBroker">And Ack</template>
          <VIcon class="ml-1" small> mdi-download </VIcon>
        </VBtn>
      </span>
    </div>
    <AgTableClient
      :column-defs="columnDefs"
      :get-row-class="getRowClass"
      :get-row-id="getRowId"
      :is-row-selectable="isRowSelectable"
      :page="1"
      :page-size="1000"
      :row-count="rowCount"
      :row-data="executions"
      :selected-items.sync="selectedItems"
      :sort="sortOn"
      table-id="MarketplaceExecutionsTable"
      @ready="onReady"
    />
  </div>
</template>

<style lang="scss" scoped>
.no-selected-items {
  .selected-items-actions {
    display: none;
  }
}

::v-deep {
  .ag-theme-material-dark {
    --ag-header-cell-hover-background-color: transparent;
    --ag-range-selection-border-color: transparent;
  }

  .unacknowledged-row {
    background-color: #ff9800 !important;
    color: black;
  }

  .ag-row-selected.unacknowledged-row {
    --ag-selected-row-background-color: hsl(36, 100%, 45%);
  }

  .ag-row {
    --ag-row-hover-color: hsl(0, 0%, 25%);
  }
  .unacknowledged-row.ag-row {
    --ag-row-hover-color: hsl(36, 100%, 45%);
  }
}
</style>
